import React, { useEffect, useRef, useState } from 'react'
import styles from "./EditImageTool.module.scss"
import { canvasToBlob, getImage, RGBToGrayScale, detectImageTransparency } from '../helpers/jsHelper';
import { copyEditConfig, resolveCopiedEditConfig } from '../helpers/imageEditsHelper';
import { getSourceDpi, writeImageDpi } from '../helpers/imageDpiHelper';
import { DEFAULT_DPI } from '../constants/renderPreviewConstants';
import { EDIT_IMAGE_TOOL_GROUP, EDIT_IMAGE_TOOL_OPTIONS, EDIT_IMAGE_TOOLS } from '../constants/constants';
import { updateRenderImageConfig } from '../helpers/api';
import { Loader } from '../common';
import { successToast, warningToast } from '../helpers/toastHelper';
import { downloadFile, downloadFileAsJPEG, downloadEditedImage } from '../api/curator';
import { ReactCompareSlider, ReactCompareSliderImage } from 'react-compare-slider';
import { dispatcher } from '../helpers/projectHelper';
import { measureToolActions, selectLastClickIsReset } from '../redux/slicers/measureToolSlicer';
import { useDataListContext } from '../contexts/DataListContext';
import { getAppState } from '../redux/store';
import { userPilotEvents } from '../common/Userpilot/userpilotHelper';
import { ColorPicker } from './admin/curator/ColorPicker/ColorPicker';
import { selectCopyInfo } from '../redux/slicers/appSlicer';
import { useSelector } from 'react-redux';

const SLIDER_MIN = -100
const SLIDER_MAX = 100

// Renders are produced as jpg, png or tiff. The editor exports in the source's
// own format so a lossless render doesn't come back as a re-compressed JPEG.
// TIFF can't be encoded by a canvas, so those fall back to PNG.
// Editing changes pixel values, so a jpg render has to be re-encoded. 0.95 keeps
// the file close to the render's own size while avoiding visible artefacts -
// quality 1 looks the same but roughly triples it.
const JPEG_QUALITY = 0.95;

function getOutputType(url) {
    const extension = url?.split('?')[0]?.split('.')?.pop()?.toLowerCase()
    return ['jpg', 'jpeg'].includes(extension) ? 'image/jpeg' : 'image/png'
}

// Add predefined color palette at the top of the file after imports
const PRESET_COLORS = [
    '#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF', '#D4BAFF',
    '#FF6B6B', '#FFA500', '#FFD700', '#4CAF50', '#2196F3', '#9C27B0',
    '#FF0000', '#FF8C00', '#FFEA00', '#28A745', '#007BFF', '#6F42C1',
    '#8B0000', '#B8860B', '#9ACD32', '#006400', '#00008B', '#4B0082',
    '#000000', '#3E2723', '#1B5E20', '#01579B', '#311B92', '#880E4F'
];

export function handleDownloadEditedImage({ event, loading, url, imageToolData, otherImage, colorPassImage, title, returnFile, dpi }) {
    return new Promise((resolve, reject) => {
        try {
            event?.stopPropagation()
            event?.preventDefault()
            loading?.(true)
            const image = new Image()
            // crossOrigin has to be set before src: a copy already sitting in
            // the browser cache from a plain <img> - a card thumbnail, the
            // viewer - loads without CORS and taints the canvas, so the export
            // throws instead of the load failing.
            image.crossOrigin = "anonymous";
            image.src = url

            image.onerror = () => {
                loading?.(false)
                reject(new Error(`Failed to load image for editing: ${url}`))
            }

            // onload and the timer below both run detached from the Promise
            // executor, so its catch can't see them. They have to settle the
            // promise and clear the loading flag themselves - otherwise a
            // failure leaves the caller awaiting forever, spinner stuck on.
            image.onload = async () => {
                try {
                    if (!image.width || !image.height) {
                        throw new Error('Image loaded with no dimensions')
                    }

                    const tempcanvas = { current: document.createElement("canvas") }

                    let otherImages = {}

                    if (imageToolData?.[EDIT_IMAGE_TOOLS.FILMIC_MODE]?.value !== imageToolData?.[EDIT_IMAGE_TOOLS.FILMIC_MODE]?.originalValue) {
                        otherImages.otherImage = await getImage(otherImage)
                    }

                    if (colorPassImage && imageToolData?.[EDIT_IMAGE_TOOLS.COLOR_CORRECTION]?.value !== imageToolData?.[EDIT_IMAGE_TOOLS.COLOR_CORRECTION]?.originalValue) {
                        otherImages.colorPass = await getImage(colorPassImage)
                    }

                    setTimeout(async () => {
                      try {
                        const editedImage = await applyActiveEffect(
                          null,
                          image,
                          image.width,
                          image.height,
                          imageToolData,
                          tempcanvas,
                          true,
                          otherImages,
                          getOutputType(url)
                        );
                        // Canvas export drops the density metadata, so re-stamp
                        // the source render's DPI before handing it on.
                        const sourceDpi = dpi || (await getSourceDpi(url)) || DEFAULT_DPI;
                        const imageUrl = URL.createObjectURL(await writeImageDpi(editedImage, sourceDpi));
                        if (!returnFile) {
                          await downloadEditedImage(imageUrl, title);
                        }
                        resolve(imageUrl);
                      } catch (error) {
                        reject(error)
                      } finally {
                        loading?.(false)
                      }
                    }, 1000);
                } catch (error) {
                    loading?.(false)
                    reject(error)
                }
            }
        }
        catch (error) {
            reject(error)
            loading?.(false)
        }
    })
}

/**
 * Applies the active image effects to the canvas in a single pixel iteration.
 *
 * @param {CanvasRenderingContext2D} ctx - The canvas rendering context.
 * @param {HTMLImageElement} img - The image to be processed.
 * @param {number} width - The width of the canvas and image.
 * @param {number} height - The height of the canvas and image.
 * @param {object} data - An object containing the effect values.
 */
export async function applyActiveEffect(ctx, img, width, height, data, tempcanvas, returnImage, otherImage, outputType) {
    const tempCtx = tempcanvas.current.getContext('2d');
    if (!tempCtx) return;

    tempcanvas.current.width = width;
    tempcanvas.current.height = height;
    tempCtx.clearRect(0, 0, width, height);
    
    const bgColor = data?.[EDIT_IMAGE_TOOLS.BACKGROUND_COLOR]?.value;
    
    // Reset composite operation
    tempCtx.globalCompositeOperation = 'source-over';
    tempCtx.globalAlpha = 1.0;
    
    // Draw background first if background color is set (assumes image has transparency)
    if (bgColor) {
        if (bgColor !== '#ffffff') {
            // Fill with solid color
            tempCtx.fillStyle = bgColor;
            tempCtx.fillRect(0, 0, width, height);
        } else {
            // Draw checkerboard pattern for transparency
            drawCheckerboard(tempCtx, width, height);
        }
    }
    
    // Draw the main image (which may have transparency)
    tempCtx.drawImage(img, 0, 0, width, height);

    // Parse once before loop
    const tempVal = data?.Temperature?.value ? parseInt(data.Temperature.value, 10) : null;
    const tintVal = data?.Tint?.value || null;
    const tintAmount = data?.Tint?.amount || 0;
    const brightnessVal = data?.Brightness?.value ? parseInt(data.Brightness.value, 10) : null;
    const contrastVal = data?.Contrast?.value ? parseInt(data.Contrast.value, 10) * 0.75 : null;
    const saturationVal = data?.Saturation?.value ? parseInt(data.Saturation.value, 10) : null;
    const vibranceVal = data?.Vibrance?.value ? parseInt(data.Vibrance.value, 10) : null;
    const shadowsVal = data?.Shadows?.value || 0;
    const highlightsVal = data?.Highlights?.value || 0;
    const blacksVal = data?.Blacks?.value ? parseInt(data.Blacks.value, 10) : null;
    const whitesVal = data?.Whites?.value ? parseInt(data.Whites.value, 10) : null;
    const vignetteVal = data?.Vignette?.value ? parseInt(data.Vignette.value) / 75 : null;
    const pass = data?.[EDIT_IMAGE_TOOLS.FILMIC_MODE]?.value && data?.[EDIT_IMAGE_TOOLS.FILMIC_MODE]?.value !== data?.[EDIT_IMAGE_TOOLS.FILMIC_MODE]?.originalValue ? data[EDIT_IMAGE_TOOLS.FILMIC_MODE] : null;
    const colorPass = data?.[EDIT_IMAGE_TOOLS.COLOR_CORRECTION]?.value && data?.[EDIT_IMAGE_TOOLS.COLOR_CORRECTION]?.value !== data?.[EDIT_IMAGE_TOOLS.COLOR_CORRECTION]?.originalValue ? data?.[EDIT_IMAGE_TOOLS.COLOR_CORRECTION] : null;

    const contrastFactor = contrastVal !== null ? (259 * (contrastVal + 255)) / (255 * (259 - contrastVal)) : null;

    const pixelCount = width * height;

    // apply passes
    if (pass && otherImage?.otherImage) {
        drawPassLayer(tempCtx, otherImage.otherImage, "source-over", pass, width, height)
    }

    if (colorPass && otherImage?.colorPass) {
        drawPassLayer(tempCtx, otherImage?.colorPass, "color", colorPass, width, height)
    }

    let imageData = tempCtx.getImageData(0, 0, width, height);
    let pixels = imageData.data;

    for (let p = 0; p < pixelCount; p++) {
        let i = p * 4;
        let x = p % width;
        let y = Math.floor(p / width);

        let r = pixels[i];
        let g = pixels[i + 1];
        let b = pixels[i + 2];

        if (tempVal !== null) [r, g, b] = applyTemperaturePixel(tempVal, r, g, b);
        if (tintVal !== null) [r, g, b] = applyTintPixel(tintVal, r, g, b, tintAmount);
        if (brightnessVal !== null) [r, g, b] = applyBrightnessPixel(brightnessVal, r, g, b);
        if (contrastFactor !== null) [r, g, b] = applyContrastPixel(contrastFactor, r, g, b);
        if (saturationVal !== null) [r, g, b] = applySaturationPixel(saturationVal, r, g, b);
        if (vibranceVal !== null) [r, g, b] = applyVibrancePixel(r, g, b, vibranceVal);
        if (shadowsVal) [r, g, b] = applyShadows(r, g, b, shadowsVal);
        if (highlightsVal) [r, g, b] = applyHighlights(r, g, b, highlightsVal);
        if (blacksVal !== null) [r, g, b] = applyBlackWhiteContrastPixel(blacksVal, 0, r, g, b, x, y, width, height);
        if (whitesVal !== null) [r, g, b] = applyBlackWhiteContrastPixel(0, whitesVal, r, g, b, x, y, width, height);
        if (vignetteVal !== null) [r, g, b] = applyVignettePixel(vignetteVal, r, g, b, x, y, width, height);

        pixels[i] = Math.min(255, Math.max(0, r));
        pixels[i + 1] = Math.min(255, Math.max(0, g));
        pixels[i + 2] = Math.min(255, Math.max(0, b));
    }

    // Sharpness and Clarity passes are still separate, you might want to optimize or parallelize these
    let finalPixels = pixels;
    if (data?.Sharpness?.value) {
        finalPixels = applySharpness(width, height, data.Sharpness.value, finalPixels);
    }
    if (data?.Clarity?.value) {
        finalPixels = applyClarity(width, height, data.Clarity.value, finalPixels);
    }

    const finalImageData = new ImageData(finalPixels, width, height);

    if (ctx) {
        ctx.putImageData(finalImageData, 0, 0);
    }

    if (returnImage) {
        tempCtx.putImageData(finalImageData, 0, 0);
        return await canvasToBlob(tempcanvas.current, outputType, outputType === 'image/jpeg' ? JPEG_QUALITY : undefined);
    }
}

const drawPassLayer = (ctx, image, blendMode, pass, width, height) => {
    let amount = (pass.value / 100);

    if (pass?.originalValue === 100) {
        amount = 1 - amount
    }

    ctx.globalCompositeOperation = blendMode;
    ctx.globalAlpha = amount;
    ctx.drawImage(image, 0, 0, width, height);
};

/**
 * Draws a checkerboard pattern for transparent background visualization
 */
function drawCheckerboard(ctx, width, height) {
    const squareSize = 10;
    const color1 = '#ffffff';
    const color2 = '#cccccc';
    
    for (let y = 0; y < height; y += squareSize) {
        for (let x = 0; x < width; x += squareSize) {
            ctx.fillStyle = ((x / squareSize) + (y / squareSize)) % 2 === 0 ? color1 : color2;
            ctx.fillRect(x, y, squareSize, squareSize);
        }
    }
}

/**
 * Applies a temperature effect to a single pixel.
 *
 * @param {number} value - The temperature value.
 * @param {number} r - The red channel value.
 * @param {number} g - The green channel value.
 * @param {number} b - The blue channel value.
 * @returns {[number, number, number]} The modified RGB values.
 */
function applyTemperaturePixel(value, r, g, b) {
    const temperatureValue = parseInt(value, 10);
    const amount = Math.abs(temperatureValue) * 0.2;
    if (temperatureValue > 0) {
        r = Math.min(255, r + amount);
        b = Math.max(0, b - amount);
    } else if (temperatureValue < 0) {
        r = Math.max(0, r - amount);
        b = Math.min(255, b + amount);
        g = Math.min(255, g + amount * 0.5); // Slightly increase Green for tint
    }
    return [r, g, b];
}

/**
 * Applies tint effect by blending pixel with tint color.
 * @param {object} value - Should contain 'rgb' and optional 'amount' (0-1)
 * @param {number} r - red channel (0-255)
 * @param {number} g - green channel (0-255)
 * @param {number} b - blue channel (0-255)
 * @returns {[number, number, number]} tinted RGB
 */
function applyTintPixel(value, r, g, b, tintAmount) {
    if (!value || !value.rgb) return [r, g, b];

    // Extract tint color components with fallback to mid-gray if missing
    const tintRgb = value.rgb;
    let tr, tg, tb;
    let amount = (tintAmount / 100 + 1) / 2

    if (Array.isArray(tintRgb)) {
        [tr, tg, tb] = tintRgb;
    } else {
        tr = typeof tintRgb.r === 'number' ? tintRgb.r : 127;
        tg = typeof tintRgb.g === 'number' ? tintRgb.g : 127;
        tb = typeof tintRgb.b === 'number' ? tintRgb.b : 127;
    }

    // Clamp tint components to [0, 255]
    tr = Math.min(255, Math.max(0, tr));
    tg = Math.min(255, Math.max(0, tg));
    tb = Math.min(255, Math.max(0, tb));

    // Tint amount: 0 (no tint) to 1 (full tint). Default 0.3 if missing or invalid
    amount = Math.min(1, Math.max(0, amount)); // clamp to [0,1]

    // Blend original pixel with tint color
    const nr = r * (1 - amount) + tr * amount;
    const ng = g * (1 - amount) + tg * amount;
    const nb = b * (1 - amount) + tb * amount;

    return [
        Math.min(255, Math.max(0, nr)),
        Math.min(255, Math.max(0, ng)),
        Math.min(255, Math.max(0, nb)),
    ];
}

/**
 * Applies a brightness effect to a single pixel.
 *
 * @param {number} value - The brightness value.
 * @param {number} r - The red channel value.
 * @param {number} g - The green channel value.
 * @param {number} b - The blue channel value.
 * @returns {[number, number, number]} The modified RGB values.
 */
function applyBrightnessPixel(value, r, g, b) {
    const amount = parseInt(value) / 250;
    r = r + r * amount;
    g = g + g * amount;
    b = b + b * amount;
    return [r, g, b];
}

/**
 * Applies a contrast effect to a single pixel.
 *
 * @param {number} factor - The contrast factor.
 * @param {number} r - The red channel value.
 * @param {number} g - The green channel value.
 * @param {number} b - The blue channel value.
 * @returns {[number, number, number]} The modified RGB values.
 */
function applyContrastPixel(factor, r, g, b) {
    r = factor * (r - 128) + 128;
    g = factor * (g - 128) + 128;
    b = factor * (b - 128) + 128;
    return [r, g, b];
}

/**
 * Applies a saturation effect to a single pixel.
 *
 * @param {number} value - The saturation value.
 * @param {number} r - The red channel value.
 * @param {number} g - The green channel value.
 * @param {number} b - The blue channel value.
 * @returns {[number, number, number]} The modified RGB values.
 */
function applySaturationPixel(value, r, g, b) {
    if (!value) {
        return [r, g, b];
    }
    const saturationValue = (parseInt(value) + 100) / 100;
    const gray = RGBToGrayScale(r, g, b);
    r = gray + saturationValue * (r - gray);
    g = gray + saturationValue * (g - gray);
    b = gray + saturationValue * (b - gray);
    return [r, g, b];
}

/**
 * Adjusts vibrance: boosts less-saturated colors more than highly saturated ones.
 * @param {number} r - Red channel (0–255)
 * @param {number} g - Green channel (0–255)
 * @param {number} b - Blue channel (0–255)
 * @param {number} vibrance - Vibrance adjustment (-100 to +100)
 * @returns {[number, number, number]} Modified RGB values
 */
function applyVibrancePixel(r, g, b, vibrance) {
    if (vibrance === 0) return [r, g, b];

    const vibranceStrength = vibrance / 100;

    // Convert to grayscale (luminance)
    const gray = RGBToGrayScale(r, g, b);

    // Calculate per-channel saturation difference from gray
    let dr = r - gray;
    let dg = g - gray;
    let db = b - gray;

    // Saturation intensity = distance from gray
    const sat = Math.sqrt(dr * dr + dg * dg + db * db) / 255;

    // Apply vibrance less to already saturated pixels
    const vibranceFactor = vibranceStrength * (1 - sat);

    r = gray + dr * (1 + vibranceFactor);
    g = gray + dg * (1 + vibranceFactor);
    b = gray + db * (1 + vibranceFactor);

    return [
        Math.max(0, Math.min(255, r)),
        Math.max(0, Math.min(255, g)),
        Math.max(0, Math.min(255, b))
    ];
}


/**
 * Applies a vignette effect to a single pixel.
 *
 * @param {number} value - The vignette value.
 * @param {number} r - The red channel value.
 * @param {number} g - The green channel value.
 * @param {number} b - The blue channel value.
 * @param {number} x - The x-coordinate of the pixel.
 * @param {number} y - The y-coordinate of the pixel.
 * @param {number} width - The width of the image.
 * @param {number} height - The height of the image.
 * @returns {[number, number, number]} The modified RGB values.
 */
function applyVignettePixel(intensity, r, g, b, x, y, width, height) {
    const centerX = width / 2;
    const centerY = height / 2;
    const maxDistance = Math.sqrt(centerX * centerX + centerY * centerY);
    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const vignette = 1 - intensity * (distance / maxDistance);

    return [
        r * vignette,
        g * vignette,
        b * vignette
    ];
}

/**
 * Applies selective black and white contrast adjustment to a single pixel.
 *
 * @param {number} blackLevel - The black level adjustment value.
 * @param {number} whiteLevel - The white level adjustment value.
 * @param {number} r - The red channel value.
 * @param {number} g - The green channel value.
 * @param {number} b - The blue channel value.
 * @param {number} x - The x-coordinate of the pixel.
 * @param {number} y - The y-coordinate of the pixel.
 * @param {number} width - The width of the image.
 * @param {number} height - The height of the image.
 * @returns {[number, number, number]} The modified RGB values.
 */
function applyBlackWhiteContrastPixel(blackLevel, whiteLevel, r, g, b) {
    const blackAmount = blackLevel / 100;  // expected 0–1
    const whiteAmount = whiteLevel / 100;  // expected 0–1
    if (blackAmount === 0 && whiteAmount === 0) return [r, g, b];

    const luminance = RGBToGrayScale(r, g, b) / 255;

    // Generate adjustment weights (smooth sigmoid)
    const shadowBoost = 1 / (1 + Math.exp(-(luminance - 0.25) * 12)); // boosts blacks
    const highlightCut = 1 / (1 + Math.exp((luminance - 0.75) * 12)); // reduces whites

    let multiplier = 1.0;

    // Blend both effects
    multiplier -= blackAmount * (1 - shadowBoost);  // darken shadows
    multiplier += whiteAmount * (1 - highlightCut); // brighten highlights

    return [
        Math.min(255, Math.max(0, r * multiplier)),
        Math.min(255, Math.max(0, g * multiplier)),
        Math.min(255, Math.max(0, b * multiplier))
    ];
}

/**
 * Shadows recovery with tonal curve enhancement.
 */
function applyShadows(r, g, b, value) {
    const shadowStrength = parseFloat(value) / 100;
    if (shadowStrength === 0) return [r, g, b];

    const luminance = RGBToGrayScale(r, g, b) / 255;
    const boost = Math.pow(1 - luminance, 2.0) * shadowStrength; // stronger boost in darker regions

    r = r - boost * (255 - r);
    g = g - boost * (255 - g);
    b = b - boost * (255 - b);

    return [r, g, b];
}

/**
 * Highlights recovery with tonal curve enhancement.
 */
function applyHighlights(r, g, b, value) {
    const highlightStrength = parseFloat(value) / 150;
    if (highlightStrength === 0) return [r, g, b];

    const luminance = RGBToGrayScale(r, g, b) / 255;
    const reduce = Math.pow(luminance, 2.0) * highlightStrength; // stronger reduction in bright regions

    r = r + reduce * r;
    g = g + reduce * g;
    b = b + reduce * b;

    return [r, g, b];
}

/**
 * Applies a sharpness effect to the image data.
 *
 * @param {number} width - The width of the image.
 * @param {number} height - The height of the image.
 * @param {string} value - The sharpness value.
 * @param {Uint8ClampedArray} imageData - The image data to be processed.
 * @returns {Uint8ClampedArray} The modified image data.
 */
function applySharpness(width, height, value, imageData) {
    const sharpnessValue = parseInt(value, 10);
    if (sharpnessValue === 0) {
        return new Uint8ClampedArray(imageData); // Return a copy if no change
    }

    const amount = sharpnessValue / 50;
    const data = new Uint8ClampedArray(imageData);
    const tempData = new Uint8ClampedArray(imageData); // Need a copy for calculations
    const kernel = [
        -amount / 9, -amount / 9, -amount / 9,
        -amount / 9, 1 + (8 * amount / 9), -amount / 9,
        -amount / 9, -amount / 9, -amount / 9
    ];

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const pixelIndex = (x + y * width) * 4;
            let r = 0, g = 0, b = 0;

            for (let ky = -1; ky <= 1; ky++) {
                for (let kx = -1; kx <= 1; kx++) {
                    const neighborX = Math.min(width - 1, Math.max(0, x + kx));
                    const neighborY = Math.min(height - 1, Math.max(0, y + ky));
                    const neighborIndex = (neighborX + neighborY * width) * 4;
                    const kernelValue = kernel[(kx + 1) + (ky + 1) * 3];

                    r += tempData[neighborIndex] * kernelValue;
                    g += tempData[neighborIndex + 1] * kernelValue;
                    b += tempData[neighborIndex + 2] * kernelValue;
                }
            }

            data[pixelIndex] = Math.max(0, Math.min(255, r));
            data[pixelIndex + 1] = Math.max(0, Math.min(255, g));
            data[pixelIndex + 2] = Math.max(0, Math.min(255, b));
        }
    }

    return data;
}

/**
 * Applies a clarity effect to the image data.
 *
 * @param {number} width - The width of the image.
 * @param {number} height - The height of the image.
 * @param {string} value - The clarity value.
 * @param {Uint8ClampedArray} imageData - The image data to be processed.
 * @returns {Uint8ClampedArray} The modified image data.
 */
function applyClarity(width, height, value, imageData) {
    const clarityValue = parseInt(value, 10);
    if (clarityValue === 0) {
        return new Uint8ClampedArray(imageData);
    }
    const amount = clarityValue / 50;
    const data = new Uint8ClampedArray(imageData);
    const tempData = new Uint8ClampedArray(imageData);

    const kernel = [
        0, -amount / 4, 0,
        -amount / 4, 1 + amount, -amount / 4,
        0, -amount / 4, 0
    ];

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const pixelIndex = (x + y * width) * 4;
            let r = 0, g = 0, b = 0;

            for (let ky = -1; ky <= 1; ky++) {
                for (let kx = -1; kx <= 1; kx++) {
                    const neighborX = Math.min(width - 1, Math.max(0, x + kx));
                    const neighborY = Math.min(height - 1, Math.max(0, y + ky));
                    const neighborIndex = (neighborX + neighborY * width) * 4;
                    const kernelValue = kernel[(kx + 1) + (ky + 1) * 3];

                    r += tempData[neighborIndex] * kernelValue;
                    g += tempData[neighborIndex + 1] * kernelValue;
                    b += tempData[neighborIndex + 2] * kernelValue;
                }
            }

            data[pixelIndex] = Math.max(0, Math.min(255, r));
            data[pixelIndex + 1] = Math.max(0, Math.min(255, g));
            data[pixelIndex + 2] = Math.max(0, Math.min(255, b));
        }
    }

    return data;
}

function EditImageTool({ id, copyId, url, data, setData, setIsEditImageTool, showOriginalImage, otherImage, colorPassImage, setAnyChangeInEditImage, callCloseFunc, pass, onImageUpdate, editImageTarget, title, onApplyingChange, ...rest }) {
    const { trackEditImageApplyConfiguration, trackClickEvent } = userPilotEvents();
    const ref = useRef()
    const wrapperRef = useRef()
    const sliderRef = useRef()
    const originalImageRef = useRef()
    const timeoutRef = useRef()
    const tempCanvasRef = useRef(document.createElement('canvas'))

    const [loading, setLoading] = useState({ canvas: true })
    const [images, setImages] = useState();
    const [focusedOption, setFocusedOption] = useState('');
    const [showColorPalette, setShowColorPalette] = useState(false);
    const [hasTransparency, setHasTransparency] = useState(false);
    const colorPickerModalRef = useRef(null);
    const dataListContext = useDataListContext();
    const copyInfo = useSelector(selectCopyInfo);

    function allImageLoaded() {
        return images?.mainImage && (otherImage ? images?.otherImage : true) && (colorPassImage ? images?.colorPass : true)
    }

    useEffect(() => {
        const processImage = () => {
            if (ref?.current && allImageLoaded()) {
                const canvas = ref.current
                const context = canvas?.getContext('2d');
                applyActiveEffect(context, images?.mainImage, canvas.width, canvas.height, data, tempCanvasRef, false, images);

                if (loading?.canvas)
                    setLoading(({ ...loading, canvas: false }))
            }

            timeoutRef.current = null
        };

        if (timeoutRef.current)
            clearTimeout(timeoutRef.current)

        timeoutRef.current = setTimeout(processImage, 10);
    }, [data, images])

    useEffect(() => {
        if (ref?.current && originalImageRef?.current) {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.src = url;

            image.onload = async () => {
                const canvas = ref.current
                const context = canvas?.getContext('2d');

                const { width: containerWidth, height: containerHeight } = wrapperRef.current.getBoundingClientRect()

                // Account for footer height (48px) when calculating available space
                const footerHeight = 48
                const availableHeight = containerHeight - footerHeight

                // Calculate canvas dimensions to maintain image aspect ratio while filling container
                const imageAspectRatio = image.width / image.height
                let canvasWidth, canvasHeight

                if (imageAspectRatio > 1) {
                    // Image is wider than tall
                    canvasWidth = containerWidth
                    canvasHeight = containerWidth / imageAspectRatio

                    if (canvasHeight > availableHeight) {
                        canvasHeight = availableHeight
                        canvasWidth = availableHeight * imageAspectRatio
                    }
                } else {
                    // Image is taller than wide
                    canvasHeight = availableHeight
                    canvasWidth = availableHeight * imageAspectRatio

                    if (canvasWidth > containerWidth) {
                        canvasWidth = containerWidth
                        canvasHeight = containerWidth / imageAspectRatio
                    }
                }

                // Set canvas dimensions (maintaining image aspect ratio)
                canvas.width = Math.round(canvasWidth)
                canvas.height = Math.round(canvasHeight)

                // Draw image to fill the canvas completely (no offset needed, canvas has correct aspect ratio)
                context.clearRect(0, 0, canvas.width, canvas.height)
                context.drawImage(image, 0, 0, canvas.width, canvas.height)

                // Set wrapper size to match canvas for the comparison slider
                originalImageRef.current.style.height = `${canvas.height}px`
                originalImageRef.current.style.width = `${canvas.width}px`
                setImages(prev => ({ ...prev, mainImage: image }))
                
                // Detect transparency
                const isTransparent = await detectImageTransparency(image);
                setHasTransparency(isTransparent);
            }

            if (otherImage) {
                const _otherImage = new Image();
                _otherImage.crossOrigin = "anonymous";
                _otherImage.src = otherImage;

                _otherImage.onload = () => {
                    setImages(prev => ({ ...prev, otherImage: _otherImage }))
                }
            }

            if (colorPassImage) {
                const _colorPassImage = new Image();
                _colorPassImage.crossOrigin = "anonymous";
                _colorPassImage.src = colorPassImage;

                _colorPassImage.onload = () => {
                    setImages(prev => ({ ...prev, colorPass: _colorPassImage }))
                }
            }
        }
    }, [ref?.current, originalImageRef?.current])

    useEffect(() => {
        return () => {
            handleResetClick?.(false)
            if (tempCanvasRef.current)
                tempCanvasRef.current?.remove();
        }
    }, [])

    function handleResetClick(isEdit = true) {
        isEdit && setAnyChangeInEditImage(true);
        const _data = {}
        Object.keys(data)?.forEach(key => {
            if (data[key]?.originalValue !== undefined) {
                _data[key] = { ...data[key], value: data[key]?.originalValue };
            } else {
                _data[key] = { value: 0 };
            }
        })
        
        setData({ ..._data, reset: true, isImageTouched: false })
    }

    function handleValueChange(event, option) {
        let value = event.target.value;
        const min = -100
        const max = 100

        // If empty or just '-', set to 0 or min/max as appropriate
        // if (value === '') {
        //     event.target.value = data[option]?.value; // Or min, depending on desired default for empty
        //     return;
        // }
        setFocusedOption(option);
        setAnyChangeInEditImage(true);
        dispatcher(measureToolActions.setLastClickedIsReset(false));
        if (value) {
          let numValue;
          if (value === '-') {
            numValue = value;
          } else {
            numValue = parseFloat(value);
          }

          // If after blur it's not a number, set to 0
          if (numValue !== '-' && isNaN(numValue)) {
              event.target.value = data[option]?.value
              return;
          }

          // Final clamp on blur to ensure it's within range
          if (numValue < min) {
            event.target.value = min;
          } else if (numValue > max) {
            event.target.value = max;
          } else {
            // Ensure the value is a clean number (e.g., no trailing decimals if not needed)
            event.target.value = numValue;
          }
        }
        const finalValue = Number(event.target.value);
        setData((prev) => ({
            ...prev,
            edited: true,
            reset: false,
            isImageTouched: true,
            [option]:
              option === EDIT_IMAGE_TOOLS.TINT
                ? { ...data?.[EDIT_IMAGE_TOOLS.TINT], amount: finalValue }
                : { ...data[option], value: finalValue },
          }));
    }

    const updateSliderBackground = (option) => {
        const min = SLIDER_MIN; // -100
        const max = SLIDER_MAX; // 100
        const value = Number(option === EDIT_IMAGE_TOOLS.TINT ? data[option]?.amount || 0 : data[option]?.value || 0);

        const zeroPos = Math.abs(min) / (max - min) * 100; // e.g., 50%

        let gradient = 'var(--dark-375)';

        if (isZeroSlider(option)) {
            if (value)
                gradient = `linear-gradient(to right, var(--primary-color) ${value}%, var(--dark-375) ${value}%)`;
        }
        else {
            if (value < 0) {
                const negPercent = Math.abs(value - 0) / (max - min) * 100;
                const leftEdge = zeroPos - negPercent;
                gradient = `linear-gradient(to right, var(--dark-375) ${leftEdge}%, var(--primary-color) ${leftEdge}%, var(--primary-color) ${zeroPos}%, var(--dark-375) ${zeroPos}%)`;
            } else if (value > 0) {
                const posPercent = (value - 0) / (max - min) * 100;
                const rightEdge = zeroPos + posPercent;
                gradient = `linear-gradient(to right, var(--dark-375) ${zeroPos}%, var(--primary-color) ${zeroPos}%, var(--primary-color) ${rightEdge}%, var(--dark-375) ${rightEdge}%)`;
            }
        }

        return gradient;
    };

    const handleCreateAnalyticPayload = () => {
      const groupKeys = {
        color: ['Filmic Mode', 'Color Correction', 'Saturation', 'Temperature'],
        details: ['Sharpness', 'Contrast'],
        light: ['Brightness', 'Highlights', 'Shadows', 'Whites', 'Blacks'],
      };

      const finalObject = { file_name: rest?.title };
      Object.entries(groupKeys).forEach(([group, keys]) => {
        const groupData = {};
        keys.forEach((key) => {
          if (data[key]) {
            groupData[key] = data[key].value;
          }
        });
        if (Object.keys(groupData).length > 0) {
          finalObject[group] = groupData;
        }
      });
      return finalObject;
    };

    /**
     * True from the moment Apply is pressed until the edited image has been
     * written. Nothing may be changed or closed in that window - the canvas
     * render is built from `data`, so a slider moved halfway through would
     * upload one picture and save a different config.
     */
    const isApplying = !!loading?.applyEffects;

    useEffect(() => {
        onApplyingChange?.(isApplying);
    }, [isApplying]);

    /** Blocks an action that would disturb an in-flight Apply. */
    function blockedWhileApplying() {
        if (!isApplying) return false;
        warningToast('Please wait - the edited image is still being saved.');
        return true;
    }

    async function handleApplyEffects() {
        if (!data?.edited || isApplying) return
        trackEditImageApplyConfiguration(handleCreateAnalyticPayload())
        const lastClickedIsReset = selectLastClickIsReset(getAppState());
        if (!lastClickedIsReset) {
            setAnyChangeInEditImage(true);
        }
        setLoading({ ...loading, applyEffects: true })
        const formData = new FormData()
        formData.append("edit_config", JSON.stringify(data?.reset ? {} : { ...data, edited: false }))
        const updateImage = await updateRenderImageConfig({ id, data: formData })
        if (updateImage?.status) {
            // Update local data with saved state (edited: false means changes are applied)
            setData({ ...data, edited: false, isImageTouched: true })
            // Refetch parent data in background without closing modal
            dataListContext?.refetchList?.();
            // Update parent's imageList with the new edit_config and generate edited image preview
            if (onImageUpdate && editImageTarget) {
                const updatedImage = {
                    ...editImageTarget,
                    render_image_details: {
                        ...editImageTarget.render_image_details,
                        edit_config: data?.reset ? {} : { ...data, edited: false, isImageTouched: true }
                    }
                };

                // Awaited, so Apply keeps spinning until the edited image has
                // actually been written - onImageUpdate hands back the upload's
                // promise. Leaving it detached told the user it was saved while
                // a multi-MB upload was still in flight, and let them close the
                // modal out from under it.
                try {
                    const editedImageUrl = await handleDownloadEditedImage({
                        url,
                        imageToolData: data?.reset ? {} : data,
                        otherImage,
                        colorPassImage,
                        title,
                        returnFile: true
                    });

                    // Pass both updated image and edited preview URL to parent
                    await onImageUpdate({
                        image: updatedImage,
                        editedImageUrl: editedImageUrl
                    });
                } catch (error) {
                    console.error('Error generating edited image preview:', error);
                    // Fallback: pass just the updated image without preview
                    await onImageUpdate({
                        image: updatedImage,
                        editedImageUrl: null
                    });
                }
            }
            // Held back to here rather than announced as soon as the config
            // saved: the edited image is written after that, and saying "saved"
            // while the loader was still turning read as a contradiction.
            successToast("Looks good! Effects are saved.")
        } else {
            console.error('API response:', updateImage)
            warningToast("Failed to save effects. Please try again.")
        }
        setLoading({ ...loading, applyEffects: false })
    }

    function isZeroSlider(option) {
        return [EDIT_IMAGE_TOOLS.COLOR_CORRECTION, EDIT_IMAGE_TOOLS.FILMIC_MODE]?.includes(option)
    }

    /**
     * Lays the copied values over this image. Filmic Mode and Colour Correction
     * blend against the render's own pass, so they keep THIS image's
     * originalValue - taking the source render's inverts the two ends of the
     * slider. Tools the copy doesn't carry are left as they are, and `reset` is
     * cleared, or an Apply straight after a paste would save an empty config.
     */
    function buildPastedConfig(copiedConfig) {
        const next = { ...copiedConfig, edited: true, isImageTouched: true, reset: false };
        [EDIT_IMAGE_TOOLS.FILMIC_MODE, EDIT_IMAGE_TOOLS.COLOR_CORRECTION].forEach(option => {
            const current = data?.[option];
            if (current?.originalValue === undefined) return;
            next[option] = { ...current, ...next[option], originalValue: current.originalValue };
        });
        return next;
    }

    // Settings copied anywhere in the app - a list card, a PDP silo/lifestyle
    // card, another tab's editor - land in copyInfo, so paste reads that first
    // and only falls back to the OS clipboard for cross-app copies.
    async function handlePasteSettings(){
        const copiedConfig = await resolveCopiedEditConfig(copyInfo);
        if (!copiedConfig) {
            warningToast("No copied settings found. Copy the edits from an edited image first.")
            return;
        }
        setAnyChangeInEditImage(true);
        dispatcher(measureToolActions.setLastClickedIsReset(false));
        setData(buildPastedConfig(copiedConfig))
        successToast("Settings pasted successfully.")
        trackClickEvent('Paste', {
          file_name: rest?.title,
          module_name: 'Image Editor',
        });
    }

    function handleBackgroundColorChange(color) {
        const colorHex = typeof color === 'string' ? color : color?.hex || color;
        setAnyChangeInEditImage(true);
        dispatcher(measureToolActions.setLastClickedIsReset(false));
        setData(prev => ({
            ...prev,
            edited: true,
            reset: false,
            [EDIT_IMAGE_TOOLS.BACKGROUND_COLOR]: { 
                value: colorHex,
                originalValue: prev[EDIT_IMAGE_TOOLS.BACKGROUND_COLOR]?.originalValue ?? '#ffffff'
            }
        }));
        // Don't close modal when color changes
    }

    function handleColorSwatchClick(color) {
        handleBackgroundColorChange(color);
        // Don't close modal when clicking preset colors
    }

    function handlePickerClick() {
        setShowColorPalette(!showColorPalette);
    }

    function handleCloseColorPicker() {
        setShowColorPalette(false);
    }

    // Close palette when clicking outside only
    useEffect(() => {
        function handleClickOutside(event) {
            // Check if click is inside the color picker component
            const isInsideColorPicker = event.target.closest('.chrome-picker');
            
            if (showColorPalette && 
                colorPickerModalRef.current && 
                !colorPickerModalRef.current.contains(event.target) &&
                !event.target.closest(`.${styles.bgColorRow}`) &&
                !isInsideColorPicker) {
                setShowColorPalette(false);
            }
        }

        if (showColorPalette) {
            // Use a slight delay to avoid capturing the initial click
            const timeoutId = setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 100);
            
            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showColorPalette]);

    // Close on Escape key
    useEffect(() => {
        function handleEscapeKey(event) {
            if (event.key === 'Escape' && showColorPalette) {
                setShowColorPalette(false);
            }
        }

        if (showColorPalette) {
            document.addEventListener('keydown', handleEscapeKey);
            return () => document.removeEventListener('keydown', handleEscapeKey);
        }
    }, [showColorPalette]);

    // Prevent closing when clicking backdrop - handle it separately
    function handleBackdropClick(e) {
        // Only close if clicking directly on backdrop, not on modal
        if (e.target === e.currentTarget) {
            setShowColorPalette(false);
        }
    }

    // Add click handler to prevent event propagation from modal content
    function handleModalContentClick(e) {
        // Stop propagation to prevent backdrop click handler
        e.stopPropagation();
    }

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <div className={styles.canvasContainer}>
                {loading?.canvas ? <Loader loading={true} /> : null}
                {/* Wrapper carries the ref (react-compare-slider@2 is a plain function
                    component and can't accept refs). It mirrors the layout context the
                    slider's own root used to provide so dragging the handle doesn't
                    cause the items to jitter. */}
                <div
                    ref={originalImageRef}
                    style={{
                        position: 'relative',
                        display: 'flex',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                    }}>
                    <ReactCompareSlider
                        position={0}
                        style={{ width: '100%', height: '100%' }}
                        itemOne={<ReactCompareSliderImage src={url} srcSet={url} alt="Image one" />}
                        itemTwo={<canvas id="__IMAGINE_EDIT_IMAGE_TOOL__" ref={ref}></canvas>}
                    />
                </div>
            </div>

            <div className={styles.sliderArea}>
                {/* Top Buttons */}
                <div className={`${styles.headerButtons} ${isApplying ? styles.disabled : ""}`}>
                    <div className={styles.actionButton} onClick={() => {
                        trackClickEvent('Reset', {
                            file_name: rest?.title,
                            module_name: 'Image Editor',
                        });
                        handleResetClick();
                        dispatcher(measureToolActions.setLastClickedIsReset(true));
                    }}>Reset</div>
                    <div className={styles.actionButton} onClick={async () => {
                        await copyEditConfig({ config: data, id: copyId ?? id, dispatch: dispatcher });
                        trackClickEvent('Copy', {
                          file_name: rest?.title,
                          module_name: 'Image Editor',
                        });
                    }}>Copy</div>
                    <div className={styles.actionButton} onClick={handlePasteSettings}>Paste</div>
                </div>

                {/* Main Slider Area */}
                <div className={`${styles.sliders} ${isApplying ? styles.disabled : ""}`}>
                    {EDIT_IMAGE_TOOL_GROUP?.map(tool => {
                        if(tool?.name === "Background" && !hasTransparency){
                          return null  
                        }
                        return <div key={`__image_tool_${tool?.name}__`} className={styles.card}>
                            <span className={styles.title}>{tool?.name}</span>
                            <span className={styles.seperator}></span>

                            {tool?.options?.map(option => {
                                // Skip Color Correction if not color pass
                                if (!colorPassImage && option === EDIT_IMAGE_TOOLS.COLOR_CORRECTION)
                                    return null

                                // Skip Remove Background option completely
                                if (option === EDIT_IMAGE_TOOLS.REMOVE_BACKGROUND) {
                                    return null;
                                }

                                // Handle Background Color picker - Only show if image has transparency
                                if (option === EDIT_IMAGE_TOOLS.BACKGROUND_COLOR) {
                                    if (!hasTransparency) {
                                        return null; // Hide background color option if no transparency
                                    }
                                    const currentColor = data[option]?.value || '#ffffff';
                                    
                                    return <div key={`__image_tool_option_${option}__`} className={styles.optionWrapper}>
                                        <div className={`${styles.info} ${styles.bgContainer}`}>
                                            <span>{option}</span>
                                            
                                            <div className={styles.bgColorRow}>
                                                <div 
                                                    className={styles.selectedColorCircle}
                                                    onClick={handlePickerClick}
                                                    style={{ backgroundColor: currentColor }}
                                                />
                                                <div 
                                                    className={styles.colorPickerButton}
                                                    onClick={handlePickerClick}
                                                >
                                                    <span>Pick Color</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }

                                return <div key={`__image_tool_option_${option}__`} className={styles.optionWrapper}>
                                    {option === 'Filmic Mode' && <div className={styles.info}>
                                        <span>{'Filmic'}</span>
                                        <input type='text' className={styles.valueBox} style={{marginLeft: '20px'}} value={focusedOption === option ? data[option]?.value : (data[option]?.value || 0)} onChange={(e) => handleValueChange(e, option)} />
                                        <span>{'Standard'}</span>
                                    </div>}
                                    {option !== 'Filmic Mode' && <div className={styles.info}>
                                        <span>{option}</span>
                                        <input type='text' className={styles.valueBox} value={focusedOption === option ? data[option]?.value : (data[option]?.value || 0)} onChange={(e) => handleValueChange(e, option)} />
                                    </div>}
                                    <div className={styles.slider}>
                                        <input ref={sliderRef} style={{ background: updateSliderBackground(option) }} className={styles.rangeInput} type="range" value={option === EDIT_IMAGE_TOOLS.TINT ? data[option]?.amount || 0 : data[option]?.value || 0} min={isZeroSlider(option) ? 0 : SLIDER_MIN} max={isZeroSlider(option) ? 100 : SLIDER_MAX} step={1} onChange={(e) => {
                                            let value = Number(e.target.value);
                                            setAnyChangeInEditImage(true);
                                            dispatcher(measureToolActions.setLastClickedIsReset(false));
                                            setData(prev => ({ ...prev, edited: true, isImageTouched: true, reset: false, [option]: option === EDIT_IMAGE_TOOLS.TINT ? { ...data?.[EDIT_IMAGE_TOOLS.TINT], amount: value } : { ...data[option], value: value } }))
                                        }} />
                                    </div>
                                </div>
                            })}

                        </div>
                    })}
                </div>

                {/* Footer buttons */}
                <div className={styles.footerButtons}>
                    <span className={styles.cancelButton} onClick={() => {
                        // trackClickEvent('Cancel', {
                        //   file_name: rest?.title,
                        //   module_name: 'Image Editor',
                        // });
                        if (blockedWhileApplying()) return;
                        callCloseFunc()
                    }}>Cancel</span>

                    <span className={`${styles.applyButton} ${!data?.edited ? styles.disabled : ""}`} onClick={handleApplyEffects}><Loader thickness={2} size={24} loading={loading?.applyEffects} borderColor="#ffffff" /> <span className={loading?.applyEffects ? styles.hidden : ""}>Apply</span></span>
                </div>
            </div>

            {/* Color Picker Modal - Only render if has transparency */}
            {showColorPalette && hasTransparency && (
                <>
                    <div className={styles.modalBackdrop} onClick={handleBackdropClick} />
                    <div 
                        className={styles.colorPaletteModal} 
                        ref={colorPickerModalRef}
                        onClick={handleModalContentClick}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <div className={styles.colorPickerModalHeader}>
                            <h3>Background Color</h3>
                            <button 
                                className={styles.colorPickerModalClose}
                                onClick={handleCloseColorPicker}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        
                        <div className={styles.colorPickerModalBody}>
                            {/* ColorPicker Component */}
                            <div 
                                className={styles.colorPickerWrapper}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ColorPicker
                                    color={data[EDIT_IMAGE_TOOLS.BACKGROUND_COLOR]?.value || '#ffffff'}
                                    onChange={(color) => handleBackgroundColorChange(color)}
                                    onChangeComplete={(color) => handleBackgroundColorChange(color)}
                                    disableAlpha
                                    inputClassName={styles.colorInputStyle}
                                />
                            </div>

                            {/* Preset Color Palette */}
                            <div className={styles.colorPaletteSection}>
                                <h4>Preset Colors</h4>
                                <div className={styles.colorPalette}>
                                    {PRESET_COLORS.map((color, index) => (
                                        <div
                                            key={`preset-color-${index}`}
                                            className={`${styles.colorSwatch} ${
                                                data[EDIT_IMAGE_TOOLS.BACKGROUND_COLOR]?.value?.toLowerCase() === color.toLowerCase() 
                                                    ? styles.active 
                                                    : ''
                                            }`}
                                            style={{ backgroundColor: color }}
                                            onClick={() => handleColorSwatchClick(color)}
                                            title={color}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default EditImageTool